import React, { useState, useMemo } from 'react';
import {
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    InputAdornment,
    Collapse,
    CircularProgress,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { useTranslation } from 'react-i18next';
import AssetService from '../../../Services/assets.service';
import DropzoneArea from '../../ClientDetails/SubComponents/DropzoneArea';
import AssetModalShell from './AssetModalShell/AssetModalShell';
import shell from './AssetModalShell/AssetModalShell.module.scss';
import { xtramInputProps } from './AssetModalShell/assetModalInputs';

const AssetImportModal = ({ open, onClose, caseId, token, refreshAssets }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    const [file, setFile] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [importResult, setImportResult] = useState(null);
    const [importYear, setImportYear] = useState('');

    const handleFileDrop = (files) => {
        if (files?.length) setFile(files[0]);
    };

    const handlePreview = async () => {
        if (!file) return;
        try {
            setProcessing(true);
            setPreviewDone(false);
            setImportResult(null);
            const res = await AssetService.PreviewAssetImport(caseId, file, token);
            const data = res?.data || res;
            setPreviewData(data);
            if (data?.detectedYear) setImportYear(String(data.detectedYear));
            const expanded = {};
            (data?.groups || []).forEach((_, i) => { expanded[i] = true; });
            setExpandedGroups(expanded);
            setPreviewDone(true);
        } catch (err) {
            console.error('Asset import preview failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleImport = async () => {
        if (!file) return;
        try {
            setProcessing(true);
            const yearNum = importYear ? parseInt(importYear, 10) : null;
            const res = await AssetService.ImportAssets(caseId, file, token, yearNum);
            setImportResult(res);
            if (refreshAssets) await refreshAssets();
        } catch (err) {
            console.error('Asset import failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreviewDone(false);
        setPreviewData(null);
        setExpandedGroups({});
        setSearchTerm('');
        setImportResult(null);
        setImportYear('');
        onClose();
    };

    const toggleGroup = (idx) => {
        setExpandedGroups(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const filteredGroups = useMemo(() => {
        if (!previewData?.groups) return [];
        if (!searchTerm.trim()) return previewData.groups;
        const term = searchTerm.trim().toLowerCase();
        return previewData.groups.map(group => ({
            ...group,
            assets: group.assets.filter(a =>
                (a.name || '').toLowerCase().includes(term) ||
                (a.description || '').toLowerCase().includes(term) ||
                String(a.originalPrice ?? '').includes(term)
            )
        })).filter(g => g.assets.length > 0 || g.groupName.toLowerCase().includes(term));
    }, [previewData, searchTerm]);

    if (!open) return null;

    const newRowsCount = previewData ? previewData.totalAssets - previewData.duplicateAssets : 0;

    return (
        <AssetModalShell
            open={open}
            onClose={handleClose}
            title={t('ImportAssets') || 'ייבוא נכסים מ-Excel'}
            subtitle={file ? `${t('FileSelected')}: ${file.name}` : null}
            direction={direction}
            size={previewDone ? 'xl' : 'sm'}
            footer={(
                <>
                    <button type="button" className={shell.secondaryBtn} onClick={handleClose}>
                        {t('Close') || 'סגור'}
                    </button>
                    <button
                        type="button"
                        className={shell.blueBtn}
                        onClick={handlePreview}
                        disabled={!file || processing}
                    >
                        {processing && !previewDone ? (t('Processing') || 'מעבד...') : (t('Preview') || 'תצוגה מקדימה')}
                    </button>
                    <button
                        type="button"
                        className={shell.primaryBtn}
                        onClick={handleImport}
                        disabled={!previewDone || processing || !!importResult}
                    >
                        {processing && previewDone ? <CircularProgress size={18} color="inherit" /> : <FileUploadIcon />}
                        {t('Import') || 'ייבוא'}
                    </button>
                </>
            )}
        >
            <div className={shell.stack}>
                <div className={shell.field}>
                    <span className={shell.fieldLabel}>{t('AssetsFile') || 'קובץ נכסים'} (.xlsx/.xls)</span>
                    <DropzoneArea
                        key={file ? file.name : 'asset-import-empty'}
                        onDrop={handleFileDrop}
                        accept=".xls,.xlsx"
                        filesLimit={1}
                    />
                </div>

                {importResult && (
                    <div className={`${shell.notice} ${importResult.success ? shell.noticeSuccess : shell.noticeDanger}`}>
                        <span>
                            {importResult.success
                                ? `${t('ImportComplete') || 'הייבוא הושלם'}: ${importResult.groupsCreated || 0} ${t('Groups') || 'קבוצות'}, ${importResult.assetsCreated || 0} ${t('Assets') || 'נכסים'}, ${importResult.duplicates || 0} ${t('DuplicateRows') || 'כפילויות'}`
                                : `${t('ImportFailed') || 'הייבוא נכשל'}: ${importResult.error || ''}`}
                        </span>
                        {importResult.errors?.length > 0 && (
                            <ul style={{ margin: 0, paddingInlineStart: 20, width: '100%' }}>
                                {importResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                            </ul>
                        )}
                    </div>
                )}

                {previewDone && previewData && (
                    <>
                        <div className={shell.toolbar}>
                            <div className={shell.stats}>
                                <span className={shell.stat}>{t('Groups') || 'קבוצות'}: <strong>{previewData.totalGroups}</strong></span>
                                <span className={shell.stat}>{t('TotalAssets') || 'סה"כ נכסים'}: <strong>{previewData.totalAssets}</strong></span>
                                {previewData.duplicateAssets > 0 && (
                                    <span className={`${shell.stat} ${shell.statOrange}`}>
                                        {t('DuplicateRows') || 'כפילויות'}: <strong>{previewData.duplicateAssets}</strong>
                                    </span>
                                )}
                                <span className={`${shell.stat} ${shell.statSuccess}`}>
                                    {t('NewRowsToImport') || 'חדשים לייבוא'}: <strong>{newRowsCount}</strong>
                                </span>
                            </div>
                            <div className={shell.toolbarEnd}>
                                <span className={shell.fieldLabel}>{t('ReportYear') || 'שנת דוח'}</span>
                                <TextField
                                    type="number"
                                    value={importYear}
                                    onChange={(e) => setImportYear(e.target.value)}
                                    variant="standard"
                                    placeholder="2024"
                                    sx={{ width: 110 }}
                                    InputProps={xtramInputProps(direction)}
                                    inputProps={{ style: { textAlign: 'center' } }}
                                />
                                <TextField
                                    placeholder={t('Search') || 'חיפוש'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    variant="standard"
                                    className={shell.searchInput}
                                    InputProps={{
                                        disableUnderline: true,
                                        startAdornment: (
                                            <InputAdornment position="start"><SearchIcon /></InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        </div>

                        {previewData.groups?.length > 0 && (
                            <div className={shell.tableWrap}>
                                <div className={shell.groupBar} style={{ cursor: 'default' }}>
                                    <CreateNewFolderIcon fontSize="small" />
                                    {t('AssetGroupsToCreate') || 'קבוצות נכסים שייווצרו'} ({previewData.groups.length})
                                </div>
                                <Table size="small" dir={direction}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: 48 }}>#</TableCell>
                                            <TableCell>{t('GroupName') || 'שם קבוצה'}</TableCell>
                                            <TableCell>{t('DepreciationPercentage') || '% פחת'}</TableCell>
                                            <TableCell>{t('AssetsCount') || 'מספר נכסים'}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewData.groups.map((group, idx) => (
                                            <TableRow key={idx} hover>
                                                <TableCell>{idx + 1}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{group.groupName}</TableCell>
                                                <TableCell>{group.depreciationPercentage}%</TableCell>
                                                <TableCell>{group.assets?.length || 0}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}

                        {filteredGroups.map((group, gIdx) => (
                            <div key={gIdx} className={shell.tableWrap} dir={direction}>
                                <div className={shell.groupBar} onClick={() => toggleGroup(gIdx)}>
                                    {expandedGroups[gIdx] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    {group.groupName}
                                    <span className={shell.groupBarMeta}>
                                        {group.assets.length} {t('Assets') || 'נכסים'} · {t('DepreciationPercentage') || '% פחת'}: {group.depreciationPercentage}%
                                    </span>
                                </div>
                                <Collapse in={expandedGroups[gIdx]}>
                                    <TableContainer sx={{ maxHeight: 400 }}>
                                        <Table size="small" stickyHeader dir={direction}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ width: 48 }}>#</TableCell>
                                                    <TableCell>{t('AssetName') || 'שם'}</TableCell>
                                                    <TableCell>{t('PurchaseDate') || 'תאריך רכישה'}</TableCell>
                                                    <TableCell>{t('ActivationDate') || 'תאריך הפעלה'}</TableCell>
                                                    <TableCell>{t('OriginalPrice') || 'מחיר מקורי'}</TableCell>
                                                    <TableCell>{t('DepreciationPercentage') || '% פחת'}</TableCell>
                                                    <TableCell>{t('AccumulatedDepreciation') || 'פחת שנצבר'}</TableCell>
                                                    <TableCell>{t('DepreciationThisYear') || 'פחת השנה'}</TableCell>
                                                    <TableCell>{t('Description') || 'הערות'}</TableCell>
                                                    <TableCell>{t('Status') || 'סטטוס'}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {group.assets.map((asset, aIdx) => (
                                                    <TableRow key={aIdx} hover className={asset.isDuplicate ? shell.rowWarning : undefined}>
                                                        <TableCell>{aIdx + 1}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>{asset.name || ''}</TableCell>
                                                        <TableCell>{asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString() : ''}</TableCell>
                                                        <TableCell>{asset.activationDate ? new Date(asset.activationDate).toLocaleDateString() : ''}</TableCell>
                                                        <TableCell>{asset.originalPrice?.toLocaleString() ?? ''}</TableCell>
                                                        <TableCell>{asset.depreciationPercentage ?? ''}</TableCell>
                                                        <TableCell>{asset.accumulatedDepreciation ? asset.accumulatedDepreciation.toLocaleString() : '—'}</TableCell>
                                                        <TableCell>{asset.depreciationThisYear ? asset.depreciationThisYear.toLocaleString() : '—'}</TableCell>
                                                        <TableCell sx={{ color: 'var(--xtram-mirage-muted)', fontSize: 12 }}>{asset.description || ''}</TableCell>
                                                        <TableCell>
                                                            <span className={`${shell.badge} ${asset.isDuplicate ? shell.badgeWarning : shell.badgeSuccess}`}>
                                                                {asset.isDuplicate ? (t('Duplicate') || 'כפילות') : (t('New') || 'חדש')}
                                                            </span>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Collapse>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </AssetModalShell>
    );
};

export default AssetImportModal;
